import { Link } from 'react-router-dom';
import { Section } from '../components/Section';
import { usePageMeta } from '../lib/usePageMeta';
import { useSettings } from '../lib/useSettings';
import { overflowBookingUrl } from '../lib/overflow';
import { EVENT, SEO } from '../seo/pages';

/**
 * The family's announcement of the move to Secrets Hideaway, published
 * 2026-09-15. The message is theirs, near enough word for word - it is not
 * paraphrased, softened or added to, and nothing here explains the
 * "misinformation" beyond what they chose to say about it. See docs/COPY.md.
 *
 * The practical block underneath is the only part written for the site: it
 * gathers the facts a reader has to act on (where, when, wristband, rooms,
 * refunds) so nobody has to mine them out of the message.
 */
export function UpdatePage() {
  usePageMeta(SEO.update.title, SEO.update.description, { jsonLd: SEO.update.jsonLd });
  const { data: s } = useSettings();
  const link = 'text-pink-300 underline hover:text-pink-200';
  const body = 'mt-5 text-lg text-ink-200';

  return (
    <>
      <Section eyebrow="15 September 2026" title="An update from the family">
        <div className="max-w-3xl">
          <p className="text-2xl text-ink-50">
            We have an important update regarding Brandon&apos;s Celebration of Life.
          </p>
          <p className={body}>
            Unfortunately, due to circumstances completely outside of our control and misinformation
            surrounding the nature of the event, we are having to change the location for
            Brandon&apos;s Celebration of Life.
          </p>
          <p className={body}>
            We are incredibly disappointed that a gathering intended solely to honor an amazing
            human being could be misrepresented as anything other than what it is:
          </p>
          <p className="mt-6">
            <span className="label-highlight text-xl sm:text-2xl">
              A celebration of Brandon&apos;s life
            </span>
          </p>
          <p className={body}>
            Our focus remains exactly where it belongs: on Brandon, Shawna, their family, their
            friends, and everyone whose life was touched by this incredible man.
          </p>

          <h2 className="mt-12 text-3xl sm:text-4xl">New location</h2>
          <p className={body}>
            Brandon&apos;s Celebration of Life will now be held at{' '}
            <strong className="text-ink-50">{EVENT.venue.name}</strong>. There will be a completely
            separate entrance specifically for Brandon&apos;s Celebration of Life.
          </p>
          <p className={body}>
            <strong className="text-ink-50">Free entry</strong> will be provided from{' '}
            <span className="text-yellow-500">7:00 PM to 10:00 PM</span> for those attending
            Brandon&apos;s Celebration. Upon arrival, you will receive a separate wristband
            specifically for the Celebration of Life.
          </p>
          <p className={body}>
            {EVENT.venue.name} is currently sold out. If you previously made hotel accommodations
            for the Celebration, please use the overflow accommodations here:{' '}
            {/* The family published the bare address, so that is what it reads;
                it points at the search for the night rather than an empty form. */}
            <a
              href={overflowBookingUrl(s?.eventStartDate)}
              target="_blank"
              rel="noopener noreferrer"
              className={link}
            >
              secretsoverflow.com
            </a>
            .
          </p>

          <p className={body}>
            While we are saddened that these changes became necessary, nothing is going to take away
            from the reason we are all coming together.
          </p>
          <p className="mt-5 text-xl text-ink-50">
            We are going to surround Shawna with love. &#10084;&#65039;
            <br />
            We are going to celebrate Brandon.
            <br />
            We are going to share stories, laugh, cry, dance, and remember the incredible human he
            was.
          </p>
          <p className={body}>And most importantly&hellip;</p>
          {/* The family wrote this line in capitals. It is not a section heading,
              so it takes the display face by hand rather than becoming an h2. */}
          <p className="mt-4 font-display text-4xl font-black uppercase leading-none sm:text-5xl">
            We are going to rock n&rsquo; roll for Brandon
          </p>
          <p className={body}>
            Thank you for your patience, understanding, and the unbelievable amount of love you
            continue to show Shawna and Brandon&apos;s family.
          </p>
          <p className="mt-5 text-xl text-ink-50">
            This is about Brandon. This is for Brandon. &#10084;&#65039;&#129304;
          </p>
          <p className="text-meta mt-8 text-xl normal-case">#BrandonStrong</p>
        </div>
      </Section>

      <section className="bg-hex border-y border-white/10 bg-night-900">
        <div className="mx-auto max-w-6xl px-4 py-16">
          <h2 className="text-3xl sm:text-4xl">What changed</h2>
          <dl className="mt-8 grid gap-8 sm:grid-cols-2">
            <div>
              <dt className="text-meta text-sm">Where</dt>
              <dd className="mt-1 text-lg text-ink-50">
                {EVENT.venue.name}
                <span className="block text-sm text-ink-400">
                  {`${EVENT.venue.street}, ${EVENT.venue.city}, ${EVENT.venue.region} ${EVENT.venue.postalCode}`}
                </span>
                <span className="mt-1 block text-sm text-ink-400">
                  Come in through the Tiki bar, on the white-fence side: that is the separate
                  entrance.
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-meta text-sm">Getting in</dt>
              <dd className="mt-1 text-lg text-ink-50">
                Free entry 7:00 to 10:00 PM
                <span className="block text-sm text-ink-400">
                  In through the Tiki bar, where you will be given a wristband. Security will be
                  around to point people the right way. There will be food, and the night does not
                  end at ten: free entry does, and staying on after that means a wristband from the
                  Front Desk, which is not free. Please{' '}
                  <Link to="/rsvp" className={link}>
                    RSVP
                  </Link>{' '}
                  with the names of your party.
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-meta text-sm">Somewhere to sleep</dt>
              <dd className="mt-1 text-lg text-ink-50">
                Overflow rooms nearby
                <span className="block text-sm text-ink-400">
                  The venue is sold out.{' '}
                  <Link to="/rooms" className={link}>
                    Where to stay
                  </Link>{' '}
                  has the details and the booking link.
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-meta text-sm">If you booked a room here</dt>
              <dd className="mt-1 text-lg text-ink-50">
                It is being refunded
                <span className="block text-sm text-ink-400">
                  Every room reservation made on this site is being refunded to the card that paid
                  for it. Questions about yours:{' '}
                  {s?.contactEmail ? (
                    <a href={`mailto:${s.contactEmail}`} className={link}>
                      {s.contactEmail}
                    </a>
                  ) : (
                    <Link to="/contact" className={link}>
                      contact the family
                    </Link>
                  )}
                  .
                </span>
              </dd>
            </div>
          </dl>
        </div>
      </section>
    </>
  );
}
